import { Alert, Col, Collapse, Divider, Row, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useObservable } from "@/utils/use-observable";
import {
    renderCurrency,
    renderDate,
    renderStatus,
} from "@/utils";
import { chargebackService } from "@/services";
import { chargebackStatusList } from "@/models/enum/chargeback-status";
import { ChargebackIssueType } from "@/models/enum/issue-type";
import Loader from "@/app/components/loader";
import { VendorCentralStatusHistoryModal } from "@/app/components/vendor-central-status-history-modal-chargeback";
import "./index.less";

const { Title, Text } = Typography;
const { Panel } = Collapse;

function VCStatusWithHistory(props: {
    status?: string;
    statusHistory?: Array<{ status: string; date: string }>;
}) {
    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const hasHistory = props.statusHistory && props.statusHistory.length > 0;

    if (hasHistory || props.status) {
        return (
            <>
                <Text
                    style={{ cursor: hasHistory ? "pointer" : "default", textDecoration: hasHistory ? "underline" : "none" }}
                    onClick={() => hasHistory && setHistoryModalVisible(true)}
                >
                    {props.status || "-"}
                </Text>
                {historyModalVisible && (
                    <VendorCentralStatusHistoryModal
                        visible={historyModalVisible}
                        onClose={() => setHistoryModalVisible(false)}
                        statusHistory={props.statusHistory}
                        currentStatus={props.status}
                    />
                )}
            </>
        );
    }

    return <Text>{props.status || "-"}</Text>;
}

export default function ChargebackPage() {
    const chargeback = useObservable(chargebackService.chargeback);
    const id = useParams().id!!;

    useEffect(() => {
        chargebackService.getChargeback(id);

        return () => {
            chargebackService.disposeChargeback();
        };
    }, [id]);

    if (!chargeback) {
        return <Loader />;
    }

    return (
        <div className="chargeback-page">
            <Title level={3}>Chargeback {chargeback.chargebackId}</Title>
            <Title level={5}>
                {chargeback.payeeCode} - {chargeback.accountName}
            </Title>
            <Divider />
            <Row className="chargeback-details">
                <Col flex={1}>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Creation Date:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{renderDate(chargeback.creationDate)}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>V.C. Status:</Text>
                        </Col>
                        <Col className="value-column">
                            <VCStatusWithHistory
                                status={chargeback.vendorCentralStatus}
                                statusHistory={chargeback.vendorCentralStatusHistory}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Status:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>
                                {renderStatus(chargebackStatusList)(chargeback.status)}
                            </Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Dispute Deadline:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{renderDate(chargeback.disputeDateEnd)}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Issue Type:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.issueType || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Financial Charge:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{renderCurrency(chargeback.financialCharge)}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Quantity:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.quantity || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Invoice Number:</Text>
                        </Col>
                        <Col className="value-column">
                            {chargeback.remittanceNumber && (
                                <Link to={`/remittances/${chargeback.remittanceNumber}`}>
                                    {chargeback.invoiceNumber || "-"}
                                </Link>
                            )}
                            {!chargeback.remittanceNumber &&
                                <Text>{chargeback.invoiceNumber || "-"}</Text>
                            }
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Reversal Invoice Number:</Text>
                        </Col>
                        <Col className="value-column">
                            {chargeback.reversalRemittanceNumber && (
                                <Link to={`/remittances/${chargeback.reversalRemittanceNumber}`}>
                                    {chargeback.reversalInvoiceNumber || "-"}
                                </Link>
                            )}
                            {!chargeback.reversalRemittanceNumber &&
                                <Text>{chargeback.reversalInvoiceNumber || "-"}</Text>
                            }
                        </Col>
                    </Row>
                </Col>
                <Col flex={1}>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Destination:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.destination || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Purchase Order:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.purchaseOrderNumber || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Package ID:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.packageId || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Tracking Number:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.trackingNumber || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>ASIN:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.asin || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>EAN:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.ean || "-"}</Text>
                        </Col>
                    </Row>
                    {chargeback.issueType === ChargebackIssueType.POOnTimeAccuracy && (
                        <>
                            <Row>
                                <Col className="label-column">
                                    <Text strong>Delivery Date:</Text>
                                </Col>
                                <Col className="value-column">
                                    <Text>{renderDate(chargeback.deliveryDate)}</Text>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="label-column">
                                    <Text strong>Shipment Delivery Window Start:</Text>
                                </Col>
                                <Col className="value-column">
                                    <Text>{renderDate(chargeback.shipmentDeliveryWindowStart)}</Text>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="label-column">
                                    <Text strong>Shipment Delivery Window End:</Text>
                                </Col>
                                <Col className="value-column">
                                    <Text>{renderDate(chargeback.shipmentDeliveryWindowEnd)}</Text>
                                </Col>
                            </Row>
                        </>
                    )}
                </Col>
                <Col flex={1}>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Notes:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{chargeback.notes || "-"}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Created At:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{renderDate(chargeback.createdAt)}</Text>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="label-column">
                            <Text strong>Updated At:</Text>
                        </Col>
                        <Col className="value-column">
                            <Text>{renderDate(chargeback.updatedAt)}</Text>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Divider />
            {chargeback.disputeDates && chargeback.disputeDates.length > 0 && (
                <div className="dispute-dates-section">
                    <Alert
                        message={
                            <div>
                                <Text strong style={{ fontSize: "16px" }}>
                                    Dispute Dates:
                                </Text>
                                <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                    {chargeback.disputeDates.map((date, index) => (
                                        <Tag key={index} color="green" style={{ fontSize: "14px", padding: "4px 12px" }}>
                                            {renderDate(date)}
                                        </Tag>
                                    ))}
                                </div>
                            </div>
                        }
                        type="success"
                        style={{ marginBottom: "24px" }}
                    />
                </div>
            )}
            <div className="reason-section">
                {chargeback.summaryReason && (
                    <div className="summary-reason">
                        <Title level={4}>Summary Reason</Title>
                        <Text>{chargeback.summaryReason}</Text>
                    </div>
                )}
                {chargeback.reason && (
                    <div className="reason">
                        <Collapse defaultActiveKey={[]}>
                            <Panel header="Reason" key="1">
                                <Text>{chargeback.reason}</Text>
                            </Panel>
                        </Collapse>
                    </div>
                )}
            </div>
        </div>
    );
}

